import { useCallback, useEffect, useRef, useState } from 'react';
import { httpGet } from '../../components/utils';

type JobStatus = 'pending' | 'completed' | 'failed';

interface Job<T = unknown> {
    jobId: string;
    status: JobStatus;
    result?: T;
    error?: string;
    errorMessage?: string;
}

const POLLING_INTERVAL = 3000; // Poll every 3 seconds

export const useJobPolling = <T = unknown>(url: string, jobId?: string | null) => {
    const [job, setJob] = useState<Job<T> | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const isCancelledRef = useRef<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchJob = useCallback(async () => {
        if (!jobId) return;

        setLoading(true);
        setError(undefined);
        setErrorMessage(undefined);

        try {
            const response = await httpGet(url);
            const data = response?.data as unknown as Job<T>;

            if (!isCancelledRef.current) {
                setJob((prevJob) => (prevJob?.jobId === data.jobId ? { ...prevJob, ...data } : data));

                if (data.status === 'failed') {
                    setErrorMessage(data.error);
                    setError(true);
                    setLoading(false);
                    return;
                }

                if (data.status !== 'pending') {
                    setLoading(false);
                    return;
                }
            }
        } catch (err: unknown) {
            if (!isCancelledRef.current) {
                const errorMessage = err instanceof Error ? err.message : 'unknown error';
                setError(true);
                setErrorMessage(errorMessage);
            }
        }

        if (!isCancelledRef.current) {
            timeoutRef.current = setTimeout(fetchJob, POLLING_INTERVAL);
        }
    }, [url, jobId]);

    useEffect(() => {
        if (!jobId) return;

        isCancelledRef.current = false;
        fetchJob();

        return () => {
            isCancelledRef.current = true;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [fetchJob, jobId]);

    return { job, loading, error, errorMessage };
};
